import { useState, useEffect } from "react";
import { 
  Map, 
  MapPin, 
  Camera, 
  Droplets, 
  X,
  Maximize2,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple SVG-based map component (no external dependencies)
const MapView = () => {
  const [open, setOpen] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchMapData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/map/data`);
      setMapData(response.data);
    } catch (error) {
      console.error("Error fetching map data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMapData();
    }
  }, [open]);

  // Convert lat/lng to SVG coordinates
  const toSvgCoords = (lat, lng, center, zoom) => {
    const scale = zoom * 1000;
    const x = 400 + (lng - center.lng) * scale;
    const y = 300 - (lat - center.lat) * scale;
    return { x, y };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'overflow': return '#ef4444';
      case 'critical': return '#f97316';
      case 'warning': return '#eab308';
      case 'low': return '#3b82f6';
      case 'normal': return '#22c55e';
      case 'online': return '#22c55e';
      case 'offline': return '#71717a';
      default: return '#71717a';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'overflow': return 'DESBORDE';
      case 'critical': return 'CRÍTICO';
      case 'warning': return 'ALERTA';
      case 'low': return 'BAJO';
      case 'normal': return 'NORMAL';
      case 'online': return 'ONLINE';
      case 'offline': return 'OFFLINE';
      default: return status?.toUpperCase() || 'DESCONOCIDO';
    }
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size="icon"
        onClick={() => setOpen(true)}
        className="text-zinc-400 hover:text-zinc-100 h-8 w-8"
        data-testid="map-view-btn"
      >
        <Map className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full bg-zinc-900 border-zinc-800 p-4">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 flex items-center gap-2">
              <Map className="w-5 h-5 text-blue-400" />
              Mapa de Ubicaciones - La Aldea de San Nicolás
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-4" style={{ height: 'calc(95vh - 100px)' }}>
            {/* Map SVG */}
            <div className="flex-1 bg-zinc-950 rounded-lg relative overflow-hidden">
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-zinc-400">Cargando mapa...</div>
                </div>
              ) : mapData ? (
                <svg 
                  viewBox="0 0 800 600" 
                  className="w-full h-full"
                  style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' }}
                >
                  {/* Background terrain */}
                  <defs>
                    <linearGradient id="terrainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#2d3748', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#1a202c', stopOpacity: 1 }} />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* Mountain/terrain shapes */}
                  <path d="M0,400 Q200,300 400,350 T800,300 L800,600 L0,600 Z" fill="#2d3748" opacity="0.5" />
                  <path d="M0,450 Q150,380 300,420 Q450,350 600,400 Q700,380 800,420 L800,600 L0,600 Z" fill="#374151" opacity="0.3" />
                  
                  {/* Grid lines */}
                  {[100, 200, 300, 400, 500, 600, 700].map(x => (
                    <line key={`v${x}`} x1={x} y1="0" x2={x} y2="600" stroke="#374151" strokeWidth="0.5" opacity="0.3" />
                  ))}
                  {[100, 200, 300, 400, 500].map(y => (
                    <line key={`h${y}`} x1="0" y1={y} x2="800" y2={y} stroke="#374151" strokeWidth="0.5" opacity="0.3" />
                  ))}
                  
                  {/* Title */}
                  <text x="400" y="40" textAnchor="middle" fill="#9ca3af" fontSize="14" fontFamily="monospace">
                    PRESAS DE LA ALDEA DE SAN NICOLÁS - GRAN CANARIA
                  </text>
                  
                  {/* Map markers */}
                  {mapData.items.map((item, index) => {
                    const coords = toSvgCoords(item.latitude, item.longitude, mapData.center, mapData.zoom);
                    const color = getStatusColor(item.status);
                    const isSelected = selectedItem?.id === item.id;
                    
                    return (
                      <g 
                        key={item.id} 
                        transform={`translate(${coords.x}, ${coords.y})`}
                        onClick={() => setSelectedItem(item)}
                        style={{ cursor: 'pointer' }}
                        filter={isSelected ? "url(#glow)" : undefined}
                      >
                        {/* Marker background circle */}
                        <circle 
                          r={isSelected ? 28 : 22} 
                          fill={color} 
                          opacity={0.2} 
                          stroke={color}
                          strokeWidth={isSelected ? 3 : 1}
                        />
                        
                        {/* Icon */}
                        {item.type === 'sensor' ? (
                          <>
                            <circle r="16" fill="#1f2937" stroke={color} strokeWidth="2" />
                            <text y="1" textAnchor="middle" fill={color} fontSize="12" fontWeight="bold">
                              💧
                            </text>
                          </>
                        ) : (
                          <>
                            <circle r="14" fill="#1f2937" stroke={color} strokeWidth="2" />
                            <text y="1" textAnchor="middle" fill={color} fontSize="10">
                              📹
                            </text>
                          </>
                        )}
                        
                        {/* Label */}
                        <text 
                          y="35" 
                          textAnchor="middle" 
                          fill="#e5e7eb" 
                          fontSize="10" 
                          fontFamily="monospace"
                          fontWeight="bold"
                        >
                          {item.name}
                        </text>
                        
                        {/* Level for sensors */}
                        {item.type === 'sensor' && (
                          <text 
                            y="48" 
                            textAnchor="middle" 
                            fill={color} 
                            fontSize="11" 
                            fontFamily="monospace"
                            fontWeight="bold"
                          >
                            {item.current_level?.toFixed(2)} {item.unit}
                          </text>
                        )}
                      </g>
                    );
                  })}
                  
                  {/* Scale */}
                  <g transform="translate(50, 550)">
                    <line x1="0" y1="0" x2="100" y2="0" stroke="#9ca3af" strokeWidth="2" />
                    <line x1="0" y1="-5" x2="0" y2="5" stroke="#9ca3af" strokeWidth="2" />
                    <line x1="100" y1="-5" x2="100" y2="5" stroke="#9ca3af" strokeWidth="2" />
                    <text x="50" y="15" textAnchor="middle" fill="#9ca3af" fontSize="10">~1 km</text>
                  </g>
                  
                  {/* Compass */}
                  <g transform="translate(750, 80)">
                    <circle r="20" fill="#1f2937" stroke="#4b5563" strokeWidth="1" />
                    <text y="5" textAnchor="middle" fill="#9ca3af" fontSize="14" fontWeight="bold">N</text>
                    <line x1="0" y1="-25" x2="0" y2="-18" stroke="#9ca3af" strokeWidth="2" />
                  </g>
                </svg>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-zinc-400">No hay datos disponibles</div>
                </div>
              )}
            </div>

            {/* Info Panel */}
            <div className="w-80 bg-zinc-800/50 rounded-lg p-4 overflow-y-auto">
              <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-4">
                {selectedItem ? 'Detalles' : 'Leyenda'}
              </h3>
              
              {selectedItem ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    {selectedItem.type === 'sensor' ? (
                      <Droplets className="w-5 h-5 text-blue-400" />
                    ) : (
                      <Camera className="w-5 h-5 text-zinc-400" />
                    )}
                    <span className="text-lg font-bold text-white">{selectedItem.name}</span>
                  </div>
                  
                  <div 
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-bold"
                    style={{ 
                      backgroundColor: `${getStatusColor(selectedItem.status)}20`,
                      color: getStatusColor(selectedItem.status)
                    }}
                  >
                    {selectedItem.status === 'normal' || selectedItem.status === 'online' ? (
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                    ) : (
                      <AlertTriangle className="w-3 h-3 mr-1" />
                    )}
                    {getStatusLabel(selectedItem.status)}
                  </div>
                  
                  {selectedItem.type === 'sensor' && (
                    <>
                      <div className="bg-zinc-900 rounded p-3">
                        <div className="text-3xl font-bold text-white font-mono">
                          {selectedItem.current_level?.toFixed(2)}
                          <span className="text-lg text-zinc-400 ml-1">{selectedItem.unit}</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">Nivel actual</div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-zinc-900 rounded p-2">
                          <div className="text-zinc-500">Máximo</div>
                          <div className="text-white font-mono">{selectedItem.max_threshold} {selectedItem.unit}</div>
                        </div>
                        {selectedItem.capacity_hm3 && (
                          <div className="bg-zinc-900 rounded p-2">
                            <div className="text-zinc-500">Capacidad</div>
                            <div className="text-white font-mono">{selectedItem.capacity_hm3} hm³</div>
                          </div>
                        )}
                        {selectedItem.basin_area_km2 && (
                          <div className="bg-zinc-900 rounded p-2">
                            <div className="text-zinc-500">Cuenca</div>
                            <div className="text-white font-mono">{selectedItem.basin_area_km2} km²</div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  
                  <div className="text-xs text-zinc-500">
                    <div>Lat: {selectedItem.latitude?.toFixed(6)}</div>
                    <div>Lng: {selectedItem.longitude?.toFixed(6)}</div>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedItem(null)}
                    className="text-zinc-400 w-full"
                  >
                    Cerrar detalles
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-emerald-500/30 border-2 border-emerald-500" />
                    <span className="text-xs text-zinc-400">Normal / Online</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-amber-500/30 border-2 border-amber-500" />
                    <span className="text-xs text-zinc-400">Alerta</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500/30 border-2 border-red-500" />
                    <span className="text-xs text-zinc-400">Crítico / Desborde</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-blue-500/30 border-2 border-blue-500" />
                    <span className="text-xs text-zinc-400">Nivel bajo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-zinc-500/30 border-2 border-zinc-500" />
                    <span className="text-xs text-zinc-400">Offline</span>
                  </div>
                  
                  <div className="border-t border-zinc-700 pt-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm">💧</span>
                      <span className="text-xs text-zinc-400">Sensor de nivel</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">📹</span>
                      <span className="text-xs text-zinc-400">Cámara</span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-zinc-500 mt-4">
                    Haz clic en un marcador para ver detalles
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MapView;
